/**
 * ExportProgressModal - Export progress and download modal
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Button } from '../../../../../ui/button';
import { Progress } from '../../../../../ui/progress';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Download,
  FileText
} from 'lucide-react';
import { downloadFileWithAuth } from '../../../../../../utils/api';
import type { ExportProgressModalProps } from '../../types';

export function ExportProgressModal({
  isOpen,
  onClose,
  status,
  progress,
  downloadUrl,
  error,
}: ExportProgressModalProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!downloadUrl) return;

    setIsDownloading(true);
    setDownloadError(null);
    try {
      await downloadFileWithAuth(downloadUrl, 'cause-report.pdf');
    } catch (err) {
      console.error('Download failed:', err);
      setDownloadError('Failed to download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md rounded-[24px] border-border/70 bg-background/95 shadow-[var(--shadow-card)] backdrop-blur-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Export Cause Report
          </DialogTitle>
          <DialogDescription>
            {status === 'generating' && 'Generating your PDF report...'}
            {status === 'ready' && 'Your report is ready for download.'}
            {status === 'error' && 'There was a problem generating your report.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center justify-center py-6 space-y-4">
          {/* Generating State */}
          {status === 'generating' && (
            <>
              <div className="relative">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
              <p className="text-sm text-muted-foreground">
                Generating report... {progress ?? 0}%
              </p>
              <Progress value={progress} className="w-full" />
            </>
          )}

          {/* Ready State */}
          {status === 'ready' && (
            <>
              <div className="rounded-full bg-primary/10 p-3">
                <CheckCircle2 className="h-12 w-12 text-primary" />
              </div>
              <p className="text-sm text-muted-foreground text-center">
                Your Cause Report PDF has been generated successfully.
              </p>
              <Button onClick={handleDownload} className="w-full" disabled={isDownloading}>
                {isDownloading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download PDF
                  </>
                )}
              </Button>
              {downloadError && (
                <p className="text-sm text-red-600 text-center">{downloadError}</p>
              )}
            </>
          )}

          {/* Error State */}
          {status === 'error' && (
            <>
              <div className="rounded-full bg-destructive/10 p-3">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
              <p className="max-w-full break-words text-center text-sm text-destructive line-clamp-3">
                {error || 'An unexpected error occurred.'}
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </div>

        {/* Footer with close option when ready */}
        {status === 'ready' && (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
